/**
 * Swarm tab — machines + agents (design/project/lib/dash2.jsx Fleet +
 * dash-desktop.jsx DMachines/DAgents, one responsive DOM). Machine cards
 * link to the detail page and show real capability chips; agent cards
 * open the AgentSheet composer. Download-menubar link + enroll
 * instructions (incl. --team when a team tenant is selected) ride along.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Agent, Machine } from "../lib/api";
import { asNamed, deleteMachine, enrollToken, machineRuntimes } from "../lib/api";
import { timeAgo } from "../lib/format";
import { Card, StatusPill, MonoChip, Icon, I } from "../kit";
import { SecLabel, EmptyState } from "./DashShell";

const DOWNLOAD_HREF = "/downloads/NightSwarm-mac-arm64-latest.dmg";
const DOWNLOAD_TITLE =
  "Signed + notarized .dmg — drag to Applications. Everything the daemon needs ships inside the app.";
const MAX_SKILL_CHIPS = 8;

/** Phase 9 §A: a seat-limit flag is stale after 5h — the plane treats it
 *  that way at read time; mirror it client-side via seatLimitedAt (an old
 *  flag without a timestamp is trusted as the plane resolved it). */
const SEAT_LIMIT_STALE_MS = 5 * 3600 * 1000;

function seatLimitedFresh(m: Machine): boolean {
  if (!m.seatLimited) return false;
  if (!m.seatLimitedAt) return true;
  const t = Date.parse(m.seatLimitedAt);
  return !Number.isFinite(t) || Date.now() - t < SEAT_LIMIT_STALE_MS;
}

// Copy-paste-complete enroll command for a machine with the app in
// /Applications: invokes the BUNDLED daemon via the app's own bundled Node
// runtime (users never install Node) and embeds the user's own JWT.
// NOTE: both paths are the sidecar staging CONTRACT (desktop/README.md —
// Contents/Resources/node-runtime/bin/node + Resources/daemon/dist).
function enrollCommand(tenantId: string | null): string {
  const node =
    '"/Applications/NightSwarm.app/Contents/Resources/node-runtime/bin/node"';
  const entry =
    '"/Applications/NightSwarm.app/Contents/Resources/daemon/dist/index.js"';
  const token = enrollToken() ?? "<sign in first>";
  return (
    `${node} ${entry} enroll --base-url https://nightswarm.app --jwt ${token}` +
    (tenantId ? ` --team ${tenantId}` : "")
  );
}

const SEAT_LIMIT_TITLE =
  "this machine's Claude account reported a usage limit — dispatch holds while pause-when-seat-limited is on; machines on a healthy seat still take \"any\" work";

function MachineCard({
  m,
  agentCount,
  onUnenroll,
  unenrolling,
}: {
  m: Machine;
  agentCount: number;
  /** tauri-desktop.md "Entitlement gating + unenroll" §4 — DELETE
   *  /machines/:id, confirm dialog naming consequences. */
  onUnenroll: (m: Machine) => void;
  unenrolling: boolean;
}) {
  const agents = (m.capabilities?.agents ?? []).map(asNamed);
  const mcps = m.capabilities?.mcps ?? [];
  const skills = (m.capabilities?.skills ?? []).map(asNamed);
  const shownSkills = skills.slice(0, MAX_SKILL_CHIPS);
  const moreSkills = skills.length - shownSkills.length;
  // Phase 12 §T9: runtime chips. Only render when the machine serves
  // something beyond plain Claude — a Claude-only machine stays clean (no
  // "claude" noise for Claude-only tenants).
  const runtimes = machineRuntimes(m);
  const showRuntimes = runtimes.some((r) => r !== "claude");
  return (
    <Link
      to={`/machines/${m.machineId}`}
      className="ns-card"
      style={{ padding: 16, opacity: m.status === "offline" ? 0.6 : 1 }}
    >
      <div className="dsh-card-head">
        <span className="dsh-tile">
          <Icon d={I.cpu} size={17} />
        </span>
        <div className="grow">
          <div className="dsh-card-name">{m.name}</div>
          <div className="dsh-card-sub">last seen {timeAgo(m.lastSeenAt)}</div>
        </div>
        {/* phase 9 §A/§D: seat-limited banner chip on affected machines */}
        {seatLimitedFresh(m) && (
          <span title={SEAT_LIMIT_TITLE}>
            <StatusPill status="seat-limited" />
          </span>
        )}
        <StatusPill status={m.status} />
      </div>
      {m.capabilities?.fullDiskAccess === false && (
        <div
          className="mono"
          style={{ fontSize: 11, color: "var(--color-warn)", marginTop: 8 }}
          title="Run `nightswarmd setup-fda` on that Mac — it sets up a dedicated daemon binary, opens the Full Disk Access panel, and confirms the grant. (macOS does not allow granting this programmatically.)"
        >
          ⚠ no Full Disk Access — Mail/Messages agents will fail
        </div>
      )}
      {(showRuntimes ||
        agents.length > 0 ||
        mcps.length > 0 ||
        skills.length > 0) && (
        <div className="dsh-chiprow">
          {showRuntimes &&
            runtimes.map((r) => (
              <span key={`rt-${r}`} title={`runtime · ${r}`}>
                <MonoChip tone="accent">{r}</MonoChip>
              </span>
            ))}
          {agents.map((a) => (
            <span key={`a-${a.name}`} title={a.description}>
              <MonoChip tone="accent">{a.name}</MonoChip>
            </span>
          ))}
          {mcps.map((mcp) => (
            <span key={`m-${mcp.name}`} title={`mcp · ${mcp.status}`}>
              <MonoChip>{mcp.name}</MonoChip>
            </span>
          ))}
          {shownSkills.map((s) => (
            <span key={`s-${s.name}`} title={s.description}>
              <MonoChip tone="ghost">{s.name}</MonoChip>
            </span>
          ))}
          {moreSkills > 0 && <MonoChip tone="ghost">+{moreSkills} more</MonoChip>}
        </div>
      )}
      <div className="dsh-card-foot">
        <span>
          {agentCount} agent{agentCount === 1 ? "" : "s"}
        </span>
        {/* unenroll lives INSIDE the Link card — stop the navigation */}
        <button
          type="button"
          className="ns-btn ns-btn--ghost ns-btn--sm"
          style={{ color: "var(--color-warn)" }}
          disabled={unenrolling}
          title="Unenroll this machine — revokes its credentials; schedules pinned to it stop being servable"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onUnenroll(m);
          }}
        >
          {unenrolling ? "unenrolling…" : "unenroll"}
        </button>
        <span className="right">details →</span>
      </div>
    </Link>
  );
}

export function SwarmTab({
  machines,
  agents,
  tenantId,
  err,
  agentsErr,
  machineName,
  onOpenAgent,
  onNewAgent,
  onMachinesChanged,
}: {
  machines: Machine[] | null;
  agents: Agent[] | null;
  /** Selected team tenant (null = personal) — threads --team into enroll. */
  tenantId: string | null;
  err: string | null;
  agentsErr: string | null;
  machineName: (machineId: string) => string;
  onOpenAgent: (agent: Agent) => void;
  onNewAgent: () => void;
  /** Refetch after an unenroll (Dashboard's refresh). */
  onMachinesChanged: () => void;
}) {
  const online = (machines ?? []).filter((m) => m.status === "online").length;

  // Unenroll (tauri-desktop.md "Entitlement gating + unenroll" §4): the
  // plane deletes the machine row AND revokes its machineToken — the
  // daemon's next WS auth fails and its tray flips to "Set up this Mac…".
  const [unenrollingId, setUnenrollingId] = useState<string | null>(null);
  const [unenrollErr, setUnenrollErr] = useState<string | null>(null);
  const unenroll = async (m: Machine) => {
    const ok = window.confirm(
      `Unenroll "${m.name}"?\n\nIts machine credentials are revoked immediately: the daemon on that Mac disconnects, schedules pinned to it stop being servable, and its running sessions end. The Mac can re-enroll any time.`
    );
    if (!ok) return;
    setUnenrollingId(m.machineId);
    setUnenrollErr(null);
    try {
      await deleteMachine(m.machineId);
      onMachinesChanged();
    } catch (e) {
      setUnenrollErr(e instanceof Error ? e.message : "unenroll failed");
    } finally {
      setUnenrollingId(null);
    }
  };
  const agentCountFor = (machineId: string) =>
    (agents ?? []).filter((a) => a.machineId === machineId).length;

  // Phase 9 §A/§B2: capacity belongs to the ACCOUNT — group machines by
  // seat when the swarm spans MORE THAN ONE seat (machines sharing one
  // Claude account pool its limits; a single-seat swarm stays flat).
  // Machines from pre-phase-9 daemons (no seatId) bucket together.
  const seatKey = (m: Machine) => m.seatId ?? "unknown-seat";
  const seatGroups = new Map<string, Machine[]>();
  for (const m of machines ?? []) {
    const k = seatKey(m);
    const g = seatGroups.get(k);
    if (g) g.push(m);
    else seatGroups.set(k, [m]);
  }
  const multiSeat = seatGroups.size > 1;
  const seatLabelOf = (members: Machine[], key: string) =>
    members.find((m) => m.seatLabel)?.seatLabel ??
    (key === "unknown-seat" ? "seat not reported" : key);

  return (
    <div className="dsh-stack">
      {/* machines */}
      <div>
        <SecLabel
          action={
            <a
              className="dsh-seclabel-action"
              href={DOWNLOAD_HREF}
              title={DOWNLOAD_TITLE}
            >
              + enroll
            </a>
          }
        >
          {machines === null
            ? "Machines"
            : `Machines · ${online} online`}
        </SecLabel>
        {err && <p className="dsh-hint-error">Couldn't load machines ({err})</p>}
        {unenrollErr && (
          <p className="dsh-hint-error">Couldn't unenroll ({unenrollErr})</p>
        )}
        {machines === null ? (
          !err && <p className="hint">Loading machines…</p>
        ) : machines.length === 0 ? (
          <Card>
            <EmptyState
              icon={I.cpu}
              title="No machines yet"
              body="Install the menubar app on a Mac you own to enroll it into your swarm."
              cta={
                <a
                  className="ns-btn ns-btn--primary"
                  href={DOWNLOAD_HREF}
                  title={DOWNLOAD_TITLE}
                >
                  <Icon d={I.download} size={14} /> Download menubar app
                </a>
              }
            />
          </Card>
        ) : multiSeat ? (
          // Phase 9 §D: >1 seat → group by seat label, with a seat-limited
          // banner chip on the group when its account reported a limit.
          <div>
            {[...seatGroups.entries()].map(([key, members]) => {
              const limited = members.some(seatLimitedFresh);
              return (
                <div key={key}>
                  <div className="dsh-seat-head">
                    <span className="label" title={`seat ${key}`}>
                      {seatLabelOf(members, key)} · {members.length} machine
                      {members.length === 1 ? "" : "s"}
                    </span>
                    {limited && (
                      <span title={SEAT_LIMIT_TITLE}>
                        <StatusPill status="seat-limited" />
                      </span>
                    )}
                  </div>
                  <div className="dsh-cards">
                    {members.map((m) => (
                      <MachineCard
                        key={m.machineId}
                        m={m}
                        agentCount={agentCountFor(m.machineId)}
                        onUnenroll={unenroll}
                        unenrolling={unenrollingId === m.machineId}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="dsh-cards">
            {machines.map((m) => (
              <MachineCard
                key={m.machineId}
                m={m}
                agentCount={agentCountFor(m.machineId)}
                onUnenroll={unenroll}
                unenrolling={unenrollingId === m.machineId}
              />
            ))}
          </div>
        )}
        <a
          className="dsh-dashed-btn"
          href={DOWNLOAD_HREF}
          title={DOWNLOAD_TITLE}
        >
          <Icon d={I.download} size={15} /> Download the menubar app (macOS,
          Apple Silicon)
        </a>
      </div>

      {/* agents */}
      <div>
        <SecLabel
          action={
            <button
              type="button"
              className="dsh-seclabel-action"
              onClick={onNewAgent}
            >
              + new agent
            </button>
          }
        >
          {agents === null ? "Agents" : `Agents · ${agents.length}`}
        </SecLabel>
        {agentsErr && (
          <p className="dsh-hint-error">Couldn't load agents ({agentsErr})</p>
        )}
        {agents === null ? (
          !agentsErr && <p className="hint">Loading agents…</p>
        ) : agents.length === 0 ? (
          <Card>
            <EmptyState
              icon={I.bot}
              title="No agents yet"
              body="An agent is a named, persistent Claude Code workspace pinned to a machine (or any). Create one, then send it a message — it routes to the agent's live session or starts a new one."
              cta={<button type="button" className="ns-btn ns-btn--primary" onClick={onNewAgent}>Create agent</button>}
            />
          </Card>
        ) : (
          <div className="dsh-cards">
            {agents.map((a) => (
              <Card
                key={a.agentName}
                style={{ cursor: "pointer" }}
                onClick={() => onOpenAgent(a)}
              >
                <div className="dsh-card-head">
                  <span className="dsh-tile round">
                    <Icon d={I.bot} size={16} />
                  </span>
                  <div className="grow">
                    <div className="dsh-card-name">{a.agentName}</div>
                    {a.description && (
                      <div className="dsh-card-sub">{a.description}</div>
                    )}
                  </div>
                  <StatusPill
                    status={a.currentSessionId ? "working" : "idleS"}
                  />
                </div>
                {a.currentSessionId && (
                  <div className="dsh-agent-task">
                    <Icon d={I.wrench} size={12} />
                    <Link
                      className="dsh-link"
                      to={`/sessions/${a.currentSessionId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      active session →
                    </Link>
                  </div>
                )}
                <div className="dsh-agent-on">
                  on{" "}
                  {a.machineId === "any"
                    ? "any machine"
                    : machineName(a.machineId)}
                  {a.warm ? " · warm" : ""}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* enroll instructions — the EASY path is one-click from the app
          itself (tray "Set up this Mac…" → /enroll/desktop loopback,
          tauri-desktop.md "One-click enrollment"). The terminal command
          stays as the HEADLESS/SSH fallback (doc §6): it embeds the
          signed-in user's own JWT and targets the daemon BUNDLED in the
          installed app, so it is copy-paste-complete on a fresh machine
          (nightswarmd is not on PATH there; "<your token>" was a dead
          end — 2026-06-11). */}
      <div>
        <SecLabel>Enroll a machine</SecLabel>
        <Card>
          <p className="hint" style={{ margin: 0 }}>
            Download the .dmg (link above), drag NightSwarm into
            Applications, and open it — choose{" "}
            <strong>“Set up this Mac…”</strong> from the menubar icon and
            it walks you through enrollment in your browser. Nothing else
            to install.
          </p>
          <p className="hint" style={{ marginBottom: 0 }}>
            Headless or SSH-only machine? Paste this into a terminal on
            that Mac instead (it includes your sign-in token and runs the
            daemon bundled inside the app):
          </p>
          <code className="dsh-enroll-code">{enrollCommand(tenantId)}</code>
          <button
            type="button"
            className="ns-btn ns-btn--sm"
            style={{ marginTop: 8 }}
            onClick={() => navigator.clipboard.writeText(enrollCommand(tenantId))}
          >
            Copy command
          </button>
          <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
            Then start the daemon from the menubar icon (or `start` from
            the same bundled CLI). Signed + notarized; the app bundles
            everything the daemon needs.
            {tenantId
              ? " Machines enrolled with --team are shared with every team member."
              : ""}
          </p>
        </Card>
      </div>
    </div>
  );
}
